<template>
  <div class="container mt-4">
    <TopNav>
      <NavItem
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.SAVE')"
        :pulse="isDirty"
        :loading="inProgress"
        @click="saveEvent()"
      />
    </TopNav>
    <div class="content mt-4" v-if="eventLoaded">
      <h1 class="mt-4 mb-4">{{ $t('EVENTS.TITLE.EDIT_EVENT') }}</h1>
      <ValidationObserver ref="form">
        <EventForm :localEvent="event" />
      </ValidationObserver>
      <div class="my-5"></div>
    </div>
  </div>
</template>

<script>
import TopNav from '@/web/components/navigation/TopNav';
import { errorToMessage, scrollToFirstFormError } from '@/core/utils/errors';
import { mapGetters } from 'vuex';
import EventForm from '@/web/components/events/EventForm';
import HMDialog from '@/web/components/common/HMDialog';
import DirtyMixin from '@/web/mixins/DirtyMixin';
import NavItem from '@/web/components/navigation/NavItem';
import { ValidationObserver } from 'vee-validate';

export default {
  name: 'EditEvent',
  mixins: [DirtyMixin],
  components: {
    TopNav,
    NavItem,
    EventForm,
    ValidationObserver,
  },
  computed: {
    ...mapGetters({
      event: 'event/event',
    }),
  },
  data() {
    return {
      eventLoaded: false,
      inProgress: false,
      wasWWWEvent: false,
    };
  },
  methods: {
    async _saveEvent(event) {
      if (this.inProgress) {
        return;
      }
      this.$refs.form.validate();
      if (this.$refs.form.flags.invalid) {
        scrollToFirstFormError();
        return;
      }
      this.inProgress = true;
      try {
        await this.$store.dispatch('event/updateEvent', event);
        this.onSave();
      } catch (error) {
        console.log(error);
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to save the event',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
      this.inProgress = false;
    },
    async saveEvent() {
      console.log('Updating event:', this.event);

      if (this.event.event_schedule) {
        // it's a repeating event

        const title = this.$t('EVENTS.TITLE.EDIT_EVENT');
        const subtitle = this.$t('EVENTS.TITLE.APPLY_CHANGES');
        const text = this.$t('EVENTS.TEXT.APPLY_EVENT_CHANGES');
        const discardButton = this.$t('EVENTS.BUTTON.JUST_THIS_EVENT');
        const confirmButton = this.$t('EVENTS.BUTTON.ALL_FUTURE_EVENTS');
        const closable = false;
        const clickToClose = false;

        const discard = () => {
          this._saveEvent(this.event);
        };
        const confirm = () => {
          this.event.apply_to_future_events = true;
          this._saveEvent(this.event);
        };

        this.$modal.show(
          HMDialog,
          {
            title,
            subtitle,
            text,
            discardButton,
            confirmButton,
            discard,
            confirm,
            closable,
          },
          { clickToClose: false }
        );
      } else {
        this._saveEvent(this.event);
      }
    },
    async onSave() {
      if (this.event.is_www_event && !this.wasWWWEvent) {
        this.$modal.show(HMDialog, {
          title: this.$t('EVENTS.TITLE.POST_ON_WELLWELLWELL'),
          htmlContent: this.$t('EVENTS.TEXT.POST_ON_WELLWELLWELL_DESCRIPTION'),
          confirmButton: this.$t('EVENTS.BUTTON.VIEW_YOUR_LISTING'),
          discardButton: this.$t('EVENTS.BUTTON.BACK_TO_CALENDAR'),
          confirm: () => {
            this.clearDirty();
            const url = `${process.env.VUE_APP_WELLWELLWELL_BASE_URL}/events/${this.event.id}`;
            window.open(url);
            this.$router.replace({ name: 'events-calendar' });
          },
          discard: () => {
            this.clearDirty();
            this.$router.replace({ name: 'events-calendar' });
          },
        });
      } else {
        this.clearDirty();
        this.$router.replace({ name: 'events-calendar' });
      }
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch(
        'event/getEventDetails',
        this.$route.params.id
      );
      this.wasWWWEvent = this.event.is_www_event;
      this.eventLoaded = true;
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load event details',
        text: errorToMessage(error.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
  },
  watch: {
    event: {
      handler: function (event, oldEvent) {
        if (oldEvent && this.eventLoaded) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss"></style>
